/**
 * WarrantyInvoiceView
 * Reuses: Dialog, Card, Badge, Separator, Button from existing UI components.
 * Reuses: generateWarrantyInvoicePDF from ../utils/warrantyInvoicePDF
 * Mirrors the layout of the existing InvoiceView.jsx — zero changes to that file.
 */
import React from "react";
import { X, Download, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { generateWarrantyInvoicePDF } from "../utils/warrantyInvoicePDF";
import { toast } from "../hooks/use-toast";

const WarrantyInvoiceView = ({ invoiceData, isOpen, onClose }) => {
  if (!invoiceData || !invoiceData.invoice) return null;

  const { invoice } = invoiceData;

  const handleDownload = () => {
    generateWarrantyInvoicePDF(invoiceData);
    toast({
      title: "Invoice Downloaded",
      description: "Your warranty invoice has been downloaded successfully.",
    });
  };

  const fmtPrice = (n) =>
    Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              <span>Warranty Invoice {invoice.invoiceNumber}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* ── Invoice Header ─────────────────────────────────────── */}
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-blue-600 mb-1">
                    Lapzop
                  </h2>
                  <p className="text-sm text-gray-600">
                    Your Trusted Laptop Partner
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">
                    NO 5 AND 6, KRISHNAPPA COMPOUND,
                  </p>
                  <p className="text-sm text-gray-600">
                    JARAGANAHALLY, J P NAGAR 7TH PHASE,
                  </p>
                  <p className="text-sm text-gray-600">KANAKAPURA MAIN ROAD,</p>
                  <p className="text-sm text-gray-600">
                    Bengaluru (Bangalore) Urban,
                  </p>
                  <p className="text-sm text-gray-600">Karnataka, 560078</p>
                  <p className="text-sm text-gray-600 mt-2">
                    Phone: +91 9353035776
                  </p>
                  <p className="text-sm text-gray-600">
                    Email: btxinwardoutward@gmail.com
                  </p>
                  <p className="text-sm text-gray-600">
                    Email: Jplapzop@gmail.com
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-start mt-6">
                <div>
                  {/* Title differs from InvoiceView — warranty renewal specific */}
                  <h3 className="text-xl font-bold mb-4">
                    TAX INVOICE – WARRANTY RENEWAL
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p>
                      <span className="font-semibold">Invoice Number:</span>{" "}
                      {invoice.invoiceNumber}
                    </p>
                    <p>
                      <span className="font-semibold">Invoice Date:</span>{" "}
                      {invoice.invoiceDate}
                    </p>
                    <p>
                      <span className="font-semibold">Renewal Ref No:</span>{" "}
                      {invoice.renewalRefNo}
                    </p>
                    <p>
                      <span className="font-semibold">Original Order No:</span>{" "}
                      {invoice.originalOrderNo}
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Bill To:</h4>
                  <div className="text-sm text-gray-700">
                    <p className="font-medium">{invoice.customer.name}</p>
                    {invoice.customer.email && <p>{invoice.customer.email}</p>}
                    {invoice.customer.phone && <p>{invoice.customer.phone}</p>}
                    {invoice.customer.address && (
                      <div className="mt-2">
                        <p>{invoice.customer.address.addressLine1}</p>
                        {invoice.customer.address.addressLine2 && (
                          <p>{invoice.customer.address.addressLine2}</p>
                        )}
                        <p>
                          {invoice.customer.address.city},{" "}
                          {invoice.customer.address.state}{" "}
                          {invoice.customer.address.postalCode}
                        </p>
                        {invoice.customer.address.country && (
                          <p>{invoice.customer.address.country}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Items Table ────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle>Renewal Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-600 text-white">
                      <th className="border p-2 text-left">Sr No</th>
                      <th className="border p-2 text-left">Product Name</th>
                      <th className="border p-2 text-center">Renewal Period</th>
                      <th className="border p-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, index) => {
                      const itemTotalWithTax = invoice.pricing.subtotal
                        ? item.lineTotal +
                          (item.lineTotal / invoice.pricing.subtotal) *
                            invoice.pricing.gstAmount
                        : item.lineTotal;

                      return (
                        <tr
                          key={index}
                          className={
                            index % 2 === 0 ? "bg-gray-50" : "bg-white"
                          }
                        >
                          <td className="border p-2">{item.srNo}</td>
                          <td className="border p-2 whitespace-pre-line">
                            {item.description}
                          </td>
                          <td className="border p-2 text-center">
                            {item.warranty || "-"}
                          </td>
                          <td className="border p-2 text-right font-semibold">
                            ₹{fmtPrice(itemTotalWithTax)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* ── Payment Info ───────────────────────────────────────── */}
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p>
                    <span className="font-semibold">Payment Method:</span>{" "}
                    {invoice.paymentMethod}
                  </p>
                  <p className="mt-2">
                    <span className="font-semibold">Payment Status:</span>{" "}
                    <Badge
                      className={`ml-2 ${invoice.paymentStatus === "PAID" ? "bg-green-500" : "bg-orange-500"}`}
                    >
                      {invoice.paymentStatus}
                    </Badge>
                  </p>
                </div>
                <div>
                  <p>
                    <span className="font-semibold">Transaction Type:</span>{" "}
                    <Badge className="ml-2 bg-blue-600">
                      {invoice.transactionType}
                    </Badge>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Terms ─────────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Terms &amp; Conditions</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-xs space-y-1 text-gray-600">
                <li>1. Warranty renewal is non-refundable once activated.</li>
                <li>2. Subject to Bangalore jurisdiction only.</li>
                <li>
                  3. Extended warranty covers as per original warranty terms.
                </li>
                <li>
                  4. Invoice generated is computer-generated and does not
                  require signature.
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* ── Action Buttons ─────────────────────────────────────── */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WarrantyInvoiceView;
